<script lang="ts">
    export interface TextMessageSentStats {
        RecipientCount: number;
        SentCount: number;
        PendingCount: number;
        BouncedCount: number;
        ClickedCount: number;
    }
</script>

<script lang="ts" setup>
    import { computed } from 'vue';
    import { formatNumericText } from '@cwd/Shared/utils/formatNumericText';

    const props = withDefaults(
        defineProps<{
            textMessage:
                | (Pick<TextMessageSentStats, 'SentCount' | 'RecipientCount' | 'PendingCount' | 'BouncedCount' | 'ClickedCount'> & {
                      [key: string]: any;
                  })
                | null;
            loading?: boolean;
            showReceivedAsCount?: boolean;
            showExactCounts?: boolean;
            journeyNodeOverlay?: boolean;
        }>(),
        {
            loading: false,
        }
    );

    const getSentCount = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.SentCount;
    });

    const getBouncedCount = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.BouncedCount;
    });

    const getReceivedCount = computed(() => {
        if (!props.textMessage) return 0;
        return getSentCount.value - getBouncedCount.value;
    });

    const getSentPercentage = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.RecipientCount === 0 ? 0 : (getSentCount.value / props.textMessage.RecipientCount) * 100;
    });

    const getReceivedPercentage = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.SentCount > 0 ? (getReceivedCount.value / props.textMessage.SentCount) * 100 : 0;
    });

    const getBouncedPercentage = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.SentCount > 0 ? (props.textMessage.BouncedCount / props.textMessage.SentCount) * 100 : 0;
    });

    const getPendingPercentage = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.RecipientCount > 0 ? (props.textMessage.PendingCount / props.textMessage.RecipientCount) * 100 : 0;
    });

    const getBouncedPercentagePrecision = computed(() => {
        return getBouncedPercentage.value > 0 && getBouncedPercentage.value < 1 ? 1 : 0;
    });

    const displaySentCount = computed(() => {
        return props.showExactCounts ? getSentCount.value : formatNumericText(getSentCount.value, { precision: 1, largeNumbers: true });
    });

    const displayReceivedCount = computed(() => {
        if (props.showReceivedAsCount) {
            return props.showExactCounts ? getReceivedCount.value : formatNumericText(getReceivedCount.value, { precision: 1, largeNumbers: true });
        }
        return formatNumericText(getReceivedPercentage.value, { precision: 0 }) + '%';
    });

    const getClickedPercentage = computed(() => {
        if (!props.textMessage) return 0;
        return props.textMessage.ClickedCount > 0 ? (props.textMessage.ClickedCount / props.textMessage.SentCount) * 100 : 0;
    });
</script>

<template>
    <div class="d-flex col-gap-lg flex-wrap" :class="{ 'ug-bars': !journeyNodeOverlay, 'node-overlay': journeyNodeOverlay }">
        <!-- Sent bar -->
        <div>
            <div class="bar-label">
                {{ $Resources.SentLabel }}
                <span class="skeleton" :class="loading && 'loading'">
                    {{ displaySentCount }}
                </span>
            </div>
            <div class="bar">
                <span class="bar-value bg-sent" :style="{ width: formatNumericText(getSentPercentage) + '%' }"></span>
            </div>
        </div>

        <!-- Pending bar -->
        <div>
            <div class="bar-label">
                {{ $Resources.SmsPendingLabel }}
                <span class="skeleton" :class="loading && 'loading'">{{ formatNumericText(getPendingPercentage) }}%</span>
            </div>
            <div class="bar">
                <span class="bar-value pending" :style="{ width: formatNumericText(getPendingPercentage) + '%' }"></span>
            </div>
        </div>

        <!-- Received bar -->
        <div>
            <div class="bar-label">
                {{ $Resources.ReceivedLabel }}
                <span class="skeleton" :class="loading && 'loading'">{{ displayReceivedCount }}</span>
            </div>
            <div class="bar">
                <span class="bar-value bg-received" :style="{ width: formatNumericText(getReceivedPercentage) + '%' }"></span>
            </div>
        </div>

        <!-- Bounced bar -->
        <div>
            <div class="bar-label">
                {{ $Resources.IssuesBouncedPercentageLabel }}
                <span class="skeleton" :class="loading && 'loading'">
                    {{ formatNumericText(getBouncedPercentage, { precision: getBouncedPercentagePrecision }) }}%
                </span>
            </div>
            <div class="bar">
                <span class="bar-value bg-bounced" :style="{ width: formatNumericText(getBouncedPercentage) + '%' }"></span>
            </div>
        </div>
    </div>
</template>

<style scoped>
    .bar-label {
        min-width: 64px;
    }
</style>
